<script setup>
/* global axios */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const isEnabled = value => value === true || value === 'true';
const normalizeQrCode = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);

  return value.base64 || value.code || value.qrcode?.base64 || '';
};
const hasConfigKey = (config, key) =>
  Object.prototype.hasOwnProperty.call(config || {}, key);

const connectionStatus = ref(
  props.inbox.provider_config?.connection_status || 'close'
);
const qrCode = ref(normalizeQrCode(props.inbox.provider_config?.qrcode));
const pairingCode = ref(props.inbox.provider_config?.pairing_code || '');
const connectionCode = ref(props.inbox.provider_config?.connection_code || '');
const manageGroups = ref(isEnabled(props.inbox.provider_config?.manage_groups));
const errorMessage = ref('');
const pollInterval = ref(null);
const isRefreshing = ref(false);
const isReconnecting = ref(false);
const isDisconnecting = ref(false);
const isRepairingWebhook = ref(false);
const isUpdatingGroupMode = ref(false);

const baseUrl = computed(
  () =>
    `/api/v1/accounts/${route.params.accountId}/inboxes/${props.inbox.id}/evolution`
);
const inboxUrl = computed(
  () => `/api/v1/accounts/${route.params.accountId}/inboxes/${props.inbox.id}`
);

const isConnected = computed(() => connectionStatus.value === 'open');
const isConnecting = computed(() => connectionStatus.value === 'connecting');
const shouldShowQr = computed(
  () => Boolean(qrCode.value) || Boolean(pairingCode.value)
);
const isAwaitingQrCode = computed(
  () => !isConnected.value && !shouldShowQr.value
);
const qrCodeSource = computed(() => {
  if (!qrCode.value) return '';

  return qrCode.value.startsWith('data:')
    ? qrCode.value
    : `data:image/png;base64,${qrCode.value}`;
});

const statusLabel = computed(() => {
  if (isConnected.value) {
    return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_STATUS_CONNECTED');
  }
  if (isConnecting.value) {
    return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_STATUS_CONNECTING');
  }
  return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_STATUS_DISCONNECTED');
});

const statusIndicatorClass = computed(() => {
  if (isConnected.value) return 'bg-n-teal-9';
  if (isConnecting.value) return 'bg-n-amber-9';
  return 'bg-n-ruby-9';
});

const stopPolling = () => {
  if (!pollInterval.value) return;

  clearInterval(pollInterval.value);
  pollInterval.value = null;
};

const applyResponse = data => {
  if (data.status) connectionStatus.value = data.status;
  if (data.qrcode) qrCode.value = normalizeQrCode(data.qrcode);
  if (data.pairing_code) pairingCode.value = data.pairing_code;
  if (data.connection_code) connectionCode.value = data.connection_code;
  if (data.error) errorMessage.value = data.error;

  if (data.status === 'open') {
    qrCode.value = '';
    pairingCode.value = '';
    connectionCode.value = '';
    stopPolling();
    store.dispatch('inboxes/get');
  }
};

const fetchQrCode = async () => {
  const { data } = await axios.get(`${baseUrl.value}/qrcode`);
  applyResponse(data);
};

const refreshStatus = async () => {
  isRefreshing.value = true;
  errorMessage.value = '';

  try {
    const { data } = await axios.get(`${baseUrl.value}/status`);
    applyResponse(data);
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_STATUS_ERROR');
  } finally {
    isRefreshing.value = false;
  }
};

const startPolling = () => {
  stopPolling();
  pollInterval.value = setInterval(async () => {
    try {
      if (!isConnected.value) await fetchQrCode();
      await refreshStatus();
    } catch {
      // Keep polling; transient Evolution failures are expected while WhatsApp reconnects.
    }
  }, 5000);
};

const loadConnection = async () => {
  errorMessage.value = '';

  try {
    if (!isConnected.value) await fetchQrCode();
    await refreshStatus();
    if (!isConnected.value) startPolling();
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_STATUS_ERROR');
  }
};

const reconnect = async () => {
  isReconnecting.value = true;
  errorMessage.value = '';
  qrCode.value = '';
  pairingCode.value = '';
  connectionCode.value = '';

  try {
    const { data } = await axios.post(`${baseUrl.value}/reconnect`);
    applyResponse(data);

    if (data.webhook_error) {
      errorMessage.value = data.webhook_error;
    }

    useAlert(
      isConnected.value
        ? t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_RECONNECTED')
        : t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_RECONNECT_STARTED')
    );

    if (!isConnected.value) startPolling();
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_RECONNECT_ERROR');
  } finally {
    isReconnecting.value = false;
  }
};

const disconnect = async () => {
  /* eslint-disable no-alert */
  const confirmed = window.confirm(
    t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_DISCONNECT_CONFIRM')
  );
  /* eslint-enable no-alert */

  if (!confirmed) {
    return;
  }

  isDisconnecting.value = true;
  errorMessage.value = '';

  try {
    const { data } = await axios.post(`${baseUrl.value}/disconnect`);
    applyResponse(data);
    qrCode.value = '';
    pairingCode.value = '';
    connectionCode.value = '';
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_DISCONNECTED'));
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_DISCONNECT_ERROR');
  } finally {
    isDisconnecting.value = false;
  }
};

const repairWebhook = async () => {
  isRepairingWebhook.value = true;
  errorMessage.value = '';

  try {
    const { data } = await axios.post(`${baseUrl.value}/repair_webhook`);
    applyResponse(data);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_WEBHOOK_REPAIRED'));
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_WEBHOOK_ERROR');
  } finally {
    isRepairingWebhook.value = false;
  }
};

const updateGroupMode = async () => {
  isUpdatingGroupMode.value = true;
  errorMessage.value = '';

  try {
    await axios.patch(inboxUrl.value, {
      channel: {
        provider_config: {
          ...(props.inbox.provider_config || {}),
          manage_groups: manageGroups.value,
        },
      },
    });
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_GROUPS_UPDATED'));
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_GROUPS_ERROR');
    manageGroups.value = !manageGroups.value;
  } finally {
    isUpdatingGroupMode.value = false;
  }
};

watch(
  () => props.inbox.provider_config,
  providerConfig => {
    connectionStatus.value = providerConfig?.connection_status || 'close';
    if (connectionStatus.value === 'open') {
      qrCode.value = '';
      pairingCode.value = '';
      connectionCode.value = '';
    } else {
      if (hasConfigKey(providerConfig, 'qrcode')) {
        qrCode.value = normalizeQrCode(providerConfig?.qrcode);
      }
      if (hasConfigKey(providerConfig, 'pairing_code')) {
        pairingCode.value = providerConfig?.pairing_code || '';
      }
      if (hasConfigKey(providerConfig, 'connection_code')) {
        connectionCode.value = providerConfig?.connection_code || '';
      }
    }
    manageGroups.value = isEnabled(providerConfig?.manage_groups);
  }
);

onMounted(loadConnection);
onUnmounted(stopPolling);
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_CONNECTION_TITLE')"
    :help-text="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_CONNECTION_SUBHEADER')"
  >
    <div class="flex flex-col gap-4">
      <div
        class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex items-center gap-2">
          <span class="size-2 rounded-full" :class="statusIndicatorClass" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ statusLabel }}
          </span>
        </div>

        <div class="flex flex-wrap gap-2">
          <NextButton
            outline
            slate
            sm
            icon="i-lucide-refresh-cw"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_REFRESH_STATUS')"
            :is-loading="isRefreshing"
            @click="refreshStatus"
          />
          <NextButton
            outline
            amber
            sm
            icon="i-lucide-wrench"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_REPAIR_WEBHOOK')"
            :is-loading="isRepairingWebhook"
            @click="repairWebhook"
          />
          <NextButton
            solid
            blue
            sm
            icon="i-lucide-rotate-cw"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_RECONNECT')"
            :is-loading="isReconnecting"
            @click="reconnect"
          />
          <NextButton
            v-if="isConnected || isConnecting"
            outline
            ruby
            sm
            icon="i-lucide-unplug"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_DISCONNECT')"
            :is-loading="isDisconnecting"
            @click="disconnect"
          />
        </div>
      </div>

      <label
        class="flex gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
      >
        <input
          v-model="manageGroups"
          type="checkbox"
          class="mt-1 size-4 flex-shrink-0"
          :disabled="isUpdatingGroupMode"
          @change="updateGroupMode"
        />
        <span class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_GROUPS_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_GROUPS_HINT') }}
          </span>
        </span>
      </label>

      <div
        v-if="shouldShowQr"
        class="grid gap-4 md:grid-cols-[auto_1fr] md:items-start"
      >
        <div
          v-if="qrCodeSource"
          class="flex size-64 items-center justify-center rounded-lg border border-n-weak bg-white p-3"
        >
          <img
            :src="qrCodeSource"
            :alt="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_QR_ALT')"
            class="size-full"
          />
        </div>

        <div class="flex min-w-0 flex-col gap-3">
          <div v-if="pairingCode" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_PAIRING_CODE') }}
            </span>
            <code
              class="break-all rounded-lg bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            >
              {{ pairingCode }}
            </code>
          </div>
          <div v-if="connectionCode" class="flex flex-col gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_CONNECTION_CODE') }}
            </span>
            <code
              class="break-all rounded-lg bg-n-alpha-2 px-3 py-2 text-sm text-n-slate-12"
            >
              {{ connectionCode }}
            </code>
          </div>
        </div>
      </div>

      <div
        v-else-if="isAwaitingQrCode"
        class="flex items-start gap-3 rounded-lg border border-dashed border-n-weak bg-n-solid-2/60 p-4"
      >
        <span
          class="i-lucide-qr-code mt-0.5 size-5 flex-shrink-0 text-n-blue-10"
        />
        <span class="flex min-w-0 flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_QR_PENDING_TITLE') }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION_QR_PENDING_DESCRIPTION')
            }}
          </span>
        </span>
      </div>

      <p v-if="errorMessage" class="mb-0 text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
    </div>
  </SettingsFieldSection>
</template>
